import { Donut } from "@/components/Donut";
import { PageHead } from "@/components/PageHead";
import { useScope } from "@/modules/connect/scope";

const OP_COLOR = { airtel: "var(--a-airtel)", jio: "var(--a-jio)", vi: "var(--a-vi)" } as const;
const STATE_META: [keyof ReturnType<typeof stateKeys>, string, string][] = [
  ["active", "Active", "var(--ok)"],
  ["idle", "Idle-active", "var(--bad)"],
  ["kyc", "KYC pending", "var(--warn)"],
  ["inventory", "Stock", "var(--t-dim)"],
  ["suspended", "Suspended", "var(--line2)"],
];
function stateKeys() {
  return { active: 0, idle: 0, kyc: 0, inventory: 0, suspended: 0 };
}

export function ConnectOverviewPage() {
  const { companies, label } = useScope();

  const sum = (f: (c: (typeof companies)[number]) => number) => companies.reduce((a, c) => a + f(c), 0);
  const totalSims = sum((c) => c.ops.airtel + c.ops.jio + c.ops.vi);
  const activated = sum((c) => c.states.active) + sum((c) => c.states.idle);

  const h24 = sum((c) => c.live.h24);
  const h48 = sum((c) => c.live.h48);
  const d7 = sum((c) => c.live.d7);
  const d30 = sum((c) => c.live.d30);

  /** Exclusive freshness buckets — cumulative windows would make 4 near-identical
   *  bars; this shows WHEN each SIM last spoke, and the tail is the problem set. */
  const buckets = [
    { l: "< 24 h", v: h24, c: "var(--teal)" },
    { l: "24–48 h", v: h48 - h24, c: "var(--info)" },
    { l: "2–7 days", v: d7 - h48, c: "var(--warn)" },
    { l: "7–30 days", v: d30 - d7, c: "var(--t-dim)" },
    { l: "Silent > 30 d", v: activated - d30, c: "var(--bad)" },
  ];

  const wMo = companies.length
    ? companies.reduce((a, c) => a + c.usageMo * (c.ops.airtel + c.ops.jio + c.ops.vi), 0) / totalSims
    : 0;
  const wDay = companies.length
    ? companies.reduce((a, c) => a + c.usageDayKB * (c.ops.airtel + c.ops.jio + c.ops.vi), 0) / totalSims
    : 0;

  return (
    <div className="mpage on">
      <PageHead eyebrow="Connect · Overview" title={`SIM estate — ${label}`} />

      <div className="section-label">Active SIMs — when did each last send data?</div>
      <div className="panel panel-pad" style={{ paddingTop: 12, paddingBottom: 12 }}>
        <div className="fresh-lane">
          {buckets.map(
            (b) =>
              b.v > 0 && (
                <span
                  key={b.l}
                  style={{ width: `${(b.v / activated) * 100}%`, background: b.c }}
                  title={`${b.l} — ${b.v.toLocaleString("en-IN")} (${Math.round((b.v / activated) * 100)}%)`}
                />
              ),
          )}
        </div>
        <div className="fresh-leg">
          {buckets.map((b) => (
            <div className="dli" key={b.l}>
              <i style={{ background: b.c }} />
              {b.l}
              <b className="num">
                {b.v.toLocaleString("en-IN")}{" "}
                <span className="pct">({Math.round((b.v / activated) * 100)}%)</span>
              </b>
            </div>
          ))}
        </div>
        <div style={{ fontSize: 10, color: "var(--t-dim)", marginTop: 8 }}>
          {activated.toLocaleString("en-IN")} activated SIMs in scope · exclusive buckets — each SIM
          counted once, by its last data session
        </div>
      </div>

      <div className="section-label">Total SIMs — {totalSims.toLocaleString("en-IN")}</div>
      <div className="chartgrid">
        <div className="panel chart">
          <div className="ct">By operator</div>
          <Donut
            slices={(["airtel", "jio", "vi"] as const).map((op) => ({
              label: op === "vi" ? "Vi" : op[0].toUpperCase() + op.slice(1),
              value: sum((c) => c.ops[op]),
              color: OP_COLOR[op],
            }))}
          />
        </div>
        <div className="panel chart">
          <div className="ct">By activation state</div>
          <Donut
            slices={STATE_META.map(([k, label, color]) => ({
              label,
              value: sum((c) => c.states[k]),
              color,
            }))}
          />
        </div>
      </div>

      <div className="section-label">Spend — last month</div>
      <div className="chartgrid">
        <div className="panel chart">
          <div className="ct">Operator-wise spend</div>
          <Donut
            slices={(["airtel", "jio", "vi"] as const).map((op) => ({
              label: op === "vi" ? "Vi" : op[0].toUpperCase() + op.slice(1),
              value: sum((c) => c.spend[op]),
              color: OP_COLOR[op],
              display: `₹${sum((c) => c.spend[op]).toLocaleString("en-IN")}`,
            }))}
          />
        </div>
        <div className="panel chart">
          <div className="ct">Cost across companies</div>
          <Donut
            slices={companies.map((c) => ({
              label: c.name,
              value: c.spend.airtel + c.spend.jio + c.spend.vi,
              color: c.color,
              display: `₹${(c.spend.airtel + c.spend.jio + c.spend.vi).toLocaleString("en-IN")}`,
            }))}
          />
        </div>
        <div className="panel chart">
          <div className="ct">SIMs across companies</div>
          <Donut
            slices={companies.map((c) => ({
              label: c.name,
              value: c.ops.airtel + c.ops.jio + c.ops.vi,
              color: c.color,
            }))}
          />
        </div>
      </div>

      <div className="section-label">Data usage — per month</div>
      <div className="chartgrid">
        <div className="panel chart">
          <div className="ct">Average per SIM · month</div>
          <div className="statbig num">
            {wMo.toFixed(1)} <span className="u">MB</span>
          </div>
          <div className="statsub">weighted across scope · trailing 3 months</div>
        </div>
        <div className="panel chart" style={{ gridColumn: "span 2", minWidth: 280 }}>
          <div className="ct">Distribution — SIMs by monthly usage</div>
          <Hist
            bars={[
              ["<5 MB", 180],
              ["5–10", 420],
              ["10–20", 388],
              ["20–30", 158],
              ["30–50", 76],
              [">50 MB", 32],
            ]}
          />
        </div>
      </div>

      <div className="section-label">Data usage — per day</div>
      <div className="chartgrid">
        <div className="panel chart">
          <div className="ct">Average per SIM · day</div>
          <div className="statbig num">
            {Math.round(wDay)} <span className="u">KB</span>
          </div>
          <div className="statsub">≈ beacons + hist drain per OLK-11 budget</div>
        </div>
        <div className="panel chart" style={{ gridColumn: "span 2", minWidth: 280 }}>
          <div className="ct">Distribution — SIMs by daily usage</div>
          <Hist
            bars={[
              ["<100 KB", 96],
              ["100–250", 312],
              ["250–500", 432],
              ["0.5–1 MB", 296],
              [">1 MB", 118],
            ]}
          />
        </div>
      </div>
    </div>
  );
}

function Hist({ bars }: { bars: [string, number][] }) {
  const max = Math.max(...bars.map(([, v]) => v));
  return (
    <div className="hist">
      {bars.map(([label, v]) => (
        <div className="hb" key={label}>
          <span className="hv num">{v}</span>
          <div className="bar" style={{ height: `${(v / max) * 100}%` }} />
          <span className="hl">{label}</span>
        </div>
      ))}
    </div>
  );
}
